import dateFns from 'date-fns';
import { QueryBuilderBase } from './QueryBuilderBase';
import { DatetimeQueryBuilder } from './DatetimeQueryBuilder';

export class DateQueryBuilder extends QueryBuilderBase {

  static get Operators() {
    return DatetimeQueryBuilder.Operators;
  }

  /**
   * Similar to DatetimeQueryBuilder, but this DateQueryBuilder
   * uses only date strings like '2016-01-30'
   *
   * @param  {mixed} operand - the value to act upon
   *                         - either a date, or a numeric value
   *                         - date: when searching absolute
   *                         - number: when searching relative
   *                                   ahead/behind by x days
   * @return {object} the query object
   */
  buildCondition(fieldName, operand, operator) {
    if (!operand && operand !== 0) return null; // Handle never occurred case separately if needed

    const condition = {};

    switch (operator) {
      case this.constructor.Operators.MORE_THAN_X_DAYS_AGO.name:
        condition[fieldName] = {
          $lt: this.pastDay(operand),
          $ne: 0,
        };
        break;
      case this.constructor.Operators.WITHIN_LAST_X_DAYS.name:
        condition[fieldName] = {
          $gte: this.pastDay(operand),
          $lte: this.pastDay(0),
        };
        break;
      case this.constructor.Operators.YESTERDAY.name:
        condition[fieldName] = {
          $eq: this.pastDay(1),
        };
        break;
      case this.constructor.Operators.TODAY.name:
        condition[fieldName] = {
          $eq: this.pastDay(0),
        };
        break;
      case this.constructor.Operators.TOMORROW.name:
        condition[fieldName] = {
          $eq: this.futureDay(1),
        };
        break;
      case this.constructor.Operators.WITHIN_NEXT_X_DAYS.name:
        condition[fieldName] = {
          $gte: this.futureDay(0),
          $lte: this.futureDay(operand),
        };
        break;
      case this.constructor.Operators.MORE_THAN_X_DAYS_FROM_NOW.name:
        condition[fieldName] = {
          $gt: this.futureDay(operand),
        };
        break;
      case this.constructor.Operators.OCCURRED_BEFORE.name:
        condition[fieldName] = {
          $lt: operand,
          $ne: 'null',
        };
        break;
      case this.constructor.Operators.OCCURRED_ON.name:
        condition[fieldName] = {
          $eq: operand,
        };
        break;
      case this.constructor.Operators.OCCURRED_AFTER.name:
        condition[fieldName] = {
          $gt: operand,
        };
        break;
      case this.constructor.Operators.NEVER_OCCURRED.name:
        condition.$or = [
          {
            [fieldName]: { $eq: 0 },
          }, {
            [fieldName]: { $eq: 'null' },
          },
        ];
        break;
      case this.constructor.Operators.HAS_OCCURRED.name:
        condition.$or = [
          {
            [fieldName]: { $ne: 0 },
          }, {
            [fieldName]: { $ne: 'null' },
          },
        ];
        break;
      default:
        throw new Error(`Unsupported operator: ${operator}`);
    }

    // Check if the condition is empty
    if (Object.keys(condition).length === 0) {
      return null;
    }

    return condition;
  }

  /**
   * From right to left:
   * - Knock the time off the date to get today's date at zero.
   * - Subtract x days to get some days in the past.
   * - Format the date again for a nice clean date.
   *
   * @param  {number} daysInPast - integer denoting the number
   *                               of days in the past
   * @return {object} datetime
   */
  pastDay(daysInPast) {
    return dateFns.format(
      dateFns.subDays(
        dateFns.parse(
          dateFns.format(new Date(), 'YYYY-MM-DD'),
        ),
        daysInPast,
      ),
      'YYYY-MM-DD',
    );
  }

  futureDay(daysInFuture) {
    return this.pastDay(-1 * daysInFuture);
  }

}
